import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_CONFIG, ApiConfig } from '../../api-config';
import { ApiResponse } from '@shared-models';
import { toCountryTitleApiPayload } from '../../../utils/country-title-catalog.utils';

interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

@Injectable({ providedIn: 'root' })
export class FiscalPersonTypeService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/countries`;
  }

  private normalizeStatus(status: string | undefined): 'active' | 'inactive' {
    if (!status) return 'active';
    return status.toLowerCase() as 'active' | 'inactive';
  }

  /**
   * LIST: Fiscal Person Types (server-side pagination)
   */
  getFiscalPersonTypes(
    countryId: string,
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
    status?: string,
  ): Observable<FiscalPersonTypePageResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }
    if (status && status.trim()) {
      params = params.set('status', status.trim().toUpperCase());
    }

    const url = `${this.apiUrl}/${countryId}/type-people`;
    return this.httpClient.get<ApiResponse<PageableData>>(url, { params }).pipe(
      map((response) => {
        const pageableData = response.data;
        const items = pageableData?.content || [];
        return {
          items: items.map((it: any) => ({
            id: it.id,
            code: it.code || it.prefix || '',
            title: it.name || it.title || '',
            status: this.normalizeStatus(it.status),
            createdAt: it.createdAt || it.created_at || '',
          })) as FiscalPersonType[],
          page: pageableData?.page ?? 0,
          size: pageableData?.size ?? size,
          totalElements: pageableData?.totalElements ?? 0,
          totalPages: pageableData?.totalPages ?? 0,
        } as FiscalPersonTypePageResponse;
      }),
    );
  }

  getFiscalPersonTypeById(
    countryId: string,
    id: string | number,
  ): Observable<FiscalPersonType> {
    const url = `${this.apiUrl}/${countryId}/type-people/${id}`;
    return this.httpClient.get<ApiResponse<any>>(url).pipe(
      map((response) => {
        const it = response.data;
        return {
          id: it.id,
          code: it.code || it.prefix || '',
          title: it.name || it.title || '',
          status: this.normalizeStatus(it.status),
          createdAt: it.createdAt || it.created_at || '',
        } as FiscalPersonType;
      }),
    );
  }

  createFiscalPersonType(
    countryId: string,
    payload: any,
  ): Observable<{ message: string }> {
    const url = `${this.apiUrl}/${countryId}/type-people`;
    return this.httpClient
      .post<{ success: boolean; message: string }>(
        url,
        toCountryTitleApiPayload(payload),
      )
      .pipe(
        map((res) => ({
          message: res.message || 'Tipo de persona fiscal creado',
        })),
      );
  }

  updateFiscalPersonType(
    countryId: string,
    id: string | number,
    payload: any,
  ): Observable<{ message: string }> {
    const url = `${this.apiUrl}/${countryId}/type-people/${id}`;
    return this.httpClient
      .put<{ success: boolean; message: string }>(
        url,
        toCountryTitleApiPayload(payload),
      )
      .pipe(
        map((res) => ({
          message: res.message || 'Tipo de persona fiscal actualizado',
        })),
      );
  }

  deleteFiscalPersonType(
    countryId: string,
    id: string | number,
  ): Observable<{ message: string }> {
    const url = `${this.apiUrl}/${countryId}/type-people/${id}`;
    return this.httpClient
      .delete<{ success: boolean; message: string }>(url)
      .pipe(
        map((res) => ({
          message: res.message || 'Tipo de persona fiscal eliminado',
        })),
      );
  }
}

export interface FiscalPersonType {
  id: string;
  code: string;
  title: string;
  status?: string;
  createdAt?: string;
}

export interface FiscalPersonTypePageResponse {
  items: FiscalPersonType[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}
